import { useEffect, useState } from "react";
import api from "@/api/client";
import Avatar from "@/components/Avatar";
import { Coffee, Clock, Users as UsersIcon, CheckCircle2 } from "lucide-react";

/**
 * Compact "Team Status Today" strip — shows who's on leave and who's on OT.
 * Scoped by the backend to the caller's team (all teams for super admins).
 */
export default function TeamStatusStrip() {
  const [status, setStatus] = useState({ date: "", members: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const { data } = await api.get("/daily-logs/team-status");
        if (alive) setStatus(data);
      } catch { /* silent */ }
      finally { if (alive) setLoading(false); }
    };
    load();
    // Refresh every 60s so managers see leave/OT changes without a manual reload
    const i = setInterval(load, 60000);
    return () => { alive = false; clearInterval(i); };
  }, []);

  if (loading) return null;

  const onLeave = status.members.filter((m) => m.on_leave);
  const halfOT = status.members.filter((m) => m.overtime === "half" && !m.on_leave);
  const fullOT = status.members.filter((m) => m.overtime === "full" && !m.on_leave);
  const otMembers = [...fullOT, ...halfOT];
  const loggedCount = status.members.filter((m) => m.logged).length;

  return (
    <div className="glass rounded-xl px-4 py-3 mb-4 flex flex-wrap items-center gap-x-6 gap-y-2" data-testid="team-status-strip">
      <div className="flex items-center gap-2 text-white/50">
        <UsersIcon size={13} className="text-[#E11D2E]" />
        <span className="overline">Team Status · Today</span>
      </div>

      {/* On leave */}
      <StatusPill
        icon={<Coffee size={11} />}
        label={onLeave.length === 0 ? "No one on leave" : `On leave · ${onLeave.length}`}
        tone="amber"
        members={onLeave}
        testId="team-status-leave"
      />

      {/* Overtime */}
      <StatusPill
        icon={<Clock size={11} />}
        label={otMembers.length === 0 ? "No overtime tonight" : `Overtime · ${otMembers.length}`}
        tone="red"
        members={otMembers}
        showOtDetails
        testId="team-status-ot"
      />

      {/* Log filed count */}
      <div className="ml-auto flex items-center gap-1.5 text-xs font-mono text-white/40" data-testid="team-status-logged">
        <CheckCircle2 size={12} className="text-emerald-400" />
        {loggedCount}/{status.members.length} logs filed
      </div>
    </div>
  );
}

function StatusPill({ icon, label, tone, members, showOtDetails, testId }) {
  const toneClass =
    tone === "amber"
      ? "text-amber-300 border-amber-500/30 bg-amber-500/5"
      : tone === "red"
      ? "text-[#E11D2E] border-[#E11D2E]/30 bg-[#E11D2E]/5"
      : "text-white/60 border-white/10";
  return (
    <div className="relative group" data-testid={testId}>
      <div className={`flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border ${toneClass}`}>
        {icon}
        <span className="font-mono">{label}</span>
      </div>
      {members.length > 0 && (
        <div className="hidden group-hover:block absolute left-0 top-full mt-1 z-30 w-64 bg-surface border border-white/10 rounded-lg p-2 shadow-2xl">
          <div className="text-[0.7rem] uppercase tracking-widest text-white/40 mb-1.5 px-1">
            {tone === "amber" ? "On leave" : "Working late"}
          </div>
          <div className="space-y-1">
            {members.map((m) => (
              <div key={m.user_id} className="flex items-center gap-2 px-1 py-1">
                <Avatar user={{ name: m.name, avatar_url: m.avatar_url }} size={22} />
                <div className="min-w-0 flex-1">
                  <div className="text-xs truncate">{m.name}</div>
                  <div className="text-[0.7rem] text-white/40 truncate">
                    {showOtDetails
                      ? `${m.overtime === "full" ? "Full Day OT" : "1/2 Day OT"}${m.overtime_client ? ` · ${m.overtime_client}` : ""}`
                      : m.title || m.team}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
